<template>
  <div class="api-settings">
    <h5>API Settings</h5>

    <md-field class="no-right-border" v-if="dataAdded">
      <label>API Token</label>
      <div class="custom-field">
        <md-input v-model="apiToken" disabled />
        <span class="input-suffix" @click="copyText(apiToken)">
          <i class="fa fa-copy" title="Copy API Token" />
        </span>
      </div>
    </md-field>

    <h5 class="sub-title">Whitelisted IPs</h5>
    <strong v-if="ipList.length === 0">Configure which IP addresses can access your account.</strong>
    <div class="ip-list">
      <table-loader v-if="!dataAdded" />
      <div v-for="(ip, index) in ipList" :key="index" class="ip">
        <p>{{ip}}</p>
        <button @click="deleteWhitelistIp(ip)">
          <i class="fa fa-trash" />Delete
        </button>
      </div>

      <div class="ip" v-if="addIp" >
        <md-field class="no-right-border" :class="{'input-error': $v.ipAddress.$error || formErrors.ip_address.length}">
          <div class="custom-field">
            <md-input v-model="ipAddress" placeholder="192.0.2.255"
                      @keyup.enter="addWhitelistIp" @blur="validateIp"
                      :ref="'IpAddress'" />
            <span class="input-suffix">
              <i class="fa fa-times-circle" @click="closeIp" />
              <i class="fa fa-check-circle" @click="addWhitelistIp" />
            </span>
          </div>
        </md-field>
        <h6 class="error-message" v-if="$v.ipAddress.$error && !$v.ipAddress.required">Please enter IP Address</h6>
        <h6 class="error-message" v-else-if="$v.ipAddress.$error && !$v.ipAddress.ipAddress">Please enter valid IP Address</h6>
        <error-info v-else :errorList="formErrors.ip_address" />
      </div>
    </div>
    <button v-if="!limitReached && !addIp && dataAdded" class="primary-btn" @click="enterIp">Add IP address</button>
    <strong v-else-if="limitReached">You have reached the maximum limit for IP whitelisting. Please contact <u>developer@hypto.in</u> to whitelist more IPs if required.</strong>
  </div>
</template>

<script>
import ErrorInfo from '@/components/global/ErrorInfo'
import TableLoader from '@/components/global/TableLoader'

import ApiServices from '@/services/api/whitelistIp'
import { copyText, checkFormErrors } from '@/utils/helpers'
import { ipAddress } from '@/utils/helpers/validators'
import { required } from 'vuelidate/lib/validators'

export default {
  name: 'SettingsApi',
  components: { ErrorInfo, TableLoader },
  data () {
    return {
      apiToken: null,
      ipList: [],
      ipAddress: null,
      limitReached: false,
      addIp: false,
      formErrors: {
        ip_address: []
      },
      dataAdded: false
    }
  },
  validations: {
    ipAddress: {
      required,
      ipAddress
    }
  },
  created () {
    this.populateData()
  },
  watch: {
    ipAddress () {
      this.formErrors.ip_address = []
    }
  },
  methods: {
    copyText,
    checkFormErrors,
    populateData () {
      this.dataAdded = false
      this.ipList = []
      ApiServices.whitelistIps().then((response) => {
        let data = response.data
        if (data) {
          this.apiToken = data.api_token
          this.ipList = data.allowed_ips
          this.limitReached = data.ips_limit_reached
          this.dataAdded = true
        }
      })
    },
    addWhitelistIp () {
      this.$v.$touch()
      if (!this.$v.$error && !this.checkFormErrors(this.formErrors)) {
        ApiServices.addWhitelistIp({ip_address: this.ipAddress}).then((response) => {
          if (response.success) {
            this.populateData()
            this.closeIp()
            this.successToastMsg(response.message)
          } else {
            this.errorToastMsg(response.message)
          }
        }).catch((errorData) => {
          let data = errorData.body
          if (typeof data.reason === 'object') {
            this.formErrors = {
              ...this.formErrors,
              ...data.reason
            }
          } else {
            this.formErrors.ip_address = [data.reason || data.message]
          }
        })
      }
    },
    enterIp () {
      this.addIp = true

      setTimeout(() => {
        this.$refs.IpAddress.$el.focus()
      })
    },
    deleteWhitelistIp (ip) {
      ApiServices.deleteWhitelistIp({ip_address: ip}).then((response) => {
        if (response.success) {
          this.populateData()
          this.successToastMsg(response.message)
        } else {
          this.errorToastMsg(response.message)
        }
      })
    },
    closeIp () {
      this.ipAddress = null
      this.addIp = false
      this.$v.ipAddress.$reset()
    },
    validateIp () {
      if (this.addIp) {
        this.$v.ipAddress.$reset()
      }
    }
  }
}
</script>

<style scoped lang="scss">
  @import '@/assets/scss/customField.scss';

  .api-settings {
    text-align: left;

    h5 {
      @include highlight-text($secondary, 15px);
      margin-bottom: 10px;
    }

    .sub-title {
      margin-top: 25px;
    }

    strong {
      @include normal-text($grey-text, 14px);
      margin-top: 10px;
      display: block;
    }

    .ip-list {
      margin: 10px 0;
      border-radius: 10px;

      .ip {
        @include custom-flex(space-between);
        flex-wrap: wrap;
        padding: 10px;
        border: $divider;

        p {
          @include normal-text($secondary, 14px);
        }

        button {
          padding: 7px 15px;
          border: 1px solid $grey-outline !important;
          @include normal-text($decrease, 13px, bold);

          .fa {
            margin-right: 10px;
          }
        }

        &:first-child {
          border-top-left-radius: 10px;
          border-top-right-radius: 10px;
        }
        &:last-child {
          border-bottom-left-radius: 10px;
          border-bottom-right-radius: 10px;
        }
      }

      .custom-field {
        width: 100%;
        @include custom-input('', 0);

        .md-input::placeholder {
          font-size: 13px;
        }
      }
    }

    .primary-btn {
      margin-left: auto;
      display: block;
    }
  }
</style>
